import { DestroyRef, Injectable } from '@angular/core';
import type { ConfirmationOptions } from './confirmation.service';

/**
 * BE-1491: вопрос «уйти со страницы с несохранёнными изменениями?» — один и тот же у гварда
 * несохранённых изменений и у кнопки «Выход».
 */
export const UNSAVED_CHANGES_CONFIRMATION: ConfirmationOptions = {
  title: 'Несохраненные изменения',
  message: 'На странице есть несохраненные изменения. Вы уверены, что хотите уйти?',
  confirmLabel: 'Уйти',
  cancelLabel: 'Остаться',
};

/**
 * Хранит набор проверок «есть ли несохранённые изменения» и навешивает
 * единственный обработчик `beforeunload` на window. Если хотя бы одна из
 * проверок возвращает true — браузер показывает нативный диалог подтверждения
 * при попытке закрыть вкладку или обновить страницу.
 *
 * Если в `register()` передать `DestroyRef`, снятие регистрации произойдёт
 * автоматически при уничтожении компонента. Иначе — вызывать возвращённую
 * функцию `unregister` вручную в `ngOnDestroy`.
 */
@Injectable({ providedIn: 'root' })
export class UnsavedChangesService {
  private readonly checks = new Set<() => boolean>();
  private listenerAttached = false;

  private readonly beforeUnloadHandler = (event: BeforeUnloadEvent): string | void => {
    if (!this.hasAnyUnsavedChanges()) {
      return;
    }
    // Текст игнорируется современными браузерами, но требуется для
    // совместимости и для срабатывания нативного диалога.
    event.preventDefault();
    event.returnValue = '';
    return '';
  };

  /**
   * Регистрирует проверку «грязного» состояния формы. Возвращает функцию
   * ручного снятия регистрации. Если передан `destroyRef` — снятие произойдёт
   * автоматически при уничтожении владельца.
   *
   * Важно: `inject(DestroyRef)` нельзя вызывать внутри сервиса в момент
   * выполнения `register()` — это не injection context. Поэтому DestroyRef
   * передаётся явно из field initializer вызывающего компонента.
   */
  register(check: () => boolean, destroyRef?: DestroyRef): () => void {
    if (typeof window === 'undefined') {
      return () => undefined;
    }

    this.checks.add(check);
    this.ensureListener();

    const unregister = () => {
      if (this.checks.delete(check)) {
        this.maybeDetachListener();
      }
    };

    destroyRef?.onDestroy(unregister);
    return unregister;
  }

  /** Есть ли несохранённое хоть на одной зарегистрированной форме (BE-1491: «Выход» спрашивает по нему). */
  hasAnyUnsavedChanges(): boolean {
    for (const check of this.checks) {
      try {
        if (check()) {
          return true;
        }
      } catch {
        // Игнорируем ошибки одиночных проверок, чтобы не сломать остальное.
      }
    }
    return false;
  }

  private ensureListener(): void {
    if (this.listenerAttached) {
      return;
    }
    window.addEventListener('beforeunload', this.beforeUnloadHandler);
    this.listenerAttached = true;
  }

  private maybeDetachListener(): void {
    if (!this.listenerAttached || this.checks.size > 0) {
      return;
    }
    window.removeEventListener('beforeunload', this.beforeUnloadHandler);
    this.listenerAttached = false;
  }
}
